import type {
  IDatasource,
  IGetRowsParams,
  SortModelItem,
} from '@mint-lib/ag-grid/community';
import type { QueryClient } from '@mint-lib/query-client';
import { Auth } from '@mint-sdk/api-auth';

import { USER_TABLE_V2_CACHE_BLOCK_SIZE } from '../../constants/userTableV2.js';

const BACKEND_ORDERING_FIELD_BY_COLUMN_ID: Record<string, string> = {
  fullName: 'full_name',
  roleName: 'role_name',
};

export type UserTableV2Row = Auth.UserItem;

type UsersQueryClient = Pick<QueryClient, 'fetchQuery'>;

type CreateUsersDatasourceParams = {
  search?: string;
  queryClient: UsersQueryClient;
};

const getBackendOrderingField = (colId: string) =>
  BACKEND_ORDERING_FIELD_BY_COLUMN_ID[colId] ?? colId;

export const getUsersOrdering = (
  sortModel: SortModelItem[] = [],
): string | undefined => {
  if (!sortModel.length) return undefined;
  const { colId, sort } = sortModel[0];
  const orderingField = getBackendOrderingField(colId);
  return sort === 'desc' ? `-${orderingField}` : orderingField;
};

export const getUsersPagination = ({
  startRow,
  endRow,
}: Pick<IGetRowsParams, 'startRow' | 'endRow'>) => ({
  offset: startRow,
  limit: endRow > startRow ? endRow - startRow : USER_TABLE_V2_CACHE_BLOCK_SIZE,
});

export const createUsersDatasource = ({
  search,
  queryClient,
}: CreateUsersDatasourceParams): IDatasource => ({
  getRows: async (params: IGetRowsParams) => {
    const { offset, limit } = getUsersPagination(params);

    try {
      const data = await queryClient.fetchQuery(
        Auth.listPaginatedUsers({
          params: {
            search: search || undefined,
            limit,
            offset,
            ordering: getUsersOrdering(params.sortModel),
            includeInheritedTags: false,
            // Labels come from the users_tags endpoint instead.
            excludeTags: true,
          },
        }),
      );

      if (params.api.isDestroyed()) {
        return;
      }

      if (data.count === 0) {
        params.api.showNoRowsOverlay();
      } else {
        params.api.hideOverlay();
      }

      params.successCallback(data.results, data.count);
    } catch {
      if (params.api.isDestroyed()) {
        return;
      }

      params.failCallback();
    }
  },
});
